import type { UserProfile, LevelProgress, ChallengeProgress, RoomProgress, Achievement } from '@/types/user.types';
import type { APIUserProfile, APIUserSettings } from '@/api/users';
import type { APITutorialProgress, APIChallengeProgress, APIRoomProgress } from '@/api/progress';
import type { APIAchievement } from '@/api/achievements';

// Profile adapters
export function apiProfileToLocal(apiProfile: APIUserProfile): UserProfile {
  return {
    id: apiProfile.userId,
    username: apiProfile.username,
    xp: apiProfile.xp,
    level: apiProfile.level,
    createdAt: new Date(apiProfile.createdAt),
    lastActive: new Date(apiProfile.lastActive),
  };
}

export function localProfileToApi(localProfile: UserProfile) {
  return {
    userId: localProfile.id,
    username: localProfile.username,
    xp: localProfile.xp,
    level: localProfile.level,
    createdAt: localProfile.createdAt.toISOString(),
    lastActive: localProfile.lastActive.toISOString(),
  };
}

// Progress adapters
export function apiTutorialProgressToLocal(apiProgress: APITutorialProgress): LevelProgress {
  return {
    levelId: apiProgress.levelId,
    completed: apiProgress.completed,
    stars: apiProgress.stars,
    attempts: apiProgress.attempts,
    completedAt: apiProgress.completedAt ? new Date(apiProgress.completedAt) : undefined,
  };
}

export function localTutorialProgressToApi(localProgress: LevelProgress): Partial<APITutorialProgress> {
  return {
    levelId: localProgress.levelId,
    completed: localProgress.completed,
    stars: localProgress.stars,
    attempts: localProgress.attempts,
    completedAt: localProgress.completedAt?.toISOString(),
  };
}

export function apiChallengeProgressToLocal(apiProgress: APIChallengeProgress): ChallengeProgress {
  return {
    challengeId: apiProgress.challengeId,
    completed: apiProgress.completed,
    bestScore: apiProgress.bestScore,
    bestTime: apiProgress.bestTime,
    attempts: apiProgress.attempts,
  };
}

export function localChallengeProgressToApi(localProgress: ChallengeProgress): Partial<APIChallengeProgress> {
  return {
    challengeId: localProgress.challengeId,
    completed: localProgress.completed,
    bestScore: localProgress.bestScore,
    bestTime: localProgress.bestTime,
    attempts: localProgress.attempts,
  };
}

export function apiRoomProgressToLocal(apiProgress: APIRoomProgress): RoomProgress {
  return {
    roomId: apiProgress.roomId,
    completed: apiProgress.completed,
    bestTime: apiProgress.bestTime,
    completedAt: apiProgress.completedAt ? new Date(apiProgress.completedAt) : undefined,
  };
}

export function localRoomProgressToApi(localProgress: RoomProgress): Partial<APIRoomProgress> {
  return {
    roomId: localProgress.roomId,
    completed: localProgress.completed,
    bestTime: localProgress.bestTime,
    puzzlesCompleted: 0, // Not tracked in current frontend
    completedAt: localProgress.completedAt?.toISOString(),
  };
}

// Achievement adapters
export function apiAchievementToLocal(apiAchievement: APIAchievement): Achievement {
  return {
    id: apiAchievement.achievementId,
    name: apiAchievement.name,
    description: apiAchievement.description,
    icon: apiAchievement.icon,
    rarity: apiAchievement.rarity,
    unlockedAt: new Date(apiAchievement.unlockedAt),
  };
}

export function localAchievementToApi(localAchievement: Achievement) {
  return {
    achievementId: localAchievement.id,
    name: localAchievement.name,
    description: localAchievement.description,
    icon: localAchievement.icon,
    rarity: localAchievement.rarity,
    unlockedAt: localAchievement.unlockedAt?.toISOString() || new Date().toISOString(),
  };
}
